import { StatTile } from "@bednbody/ui/components/stat-tile";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { cn } from "@bednbody/ui/lib/utils";
import { isOutstandingCash } from "@/features/cash/cash.custody";
import { ArrowRightIcon, WalletIcon, CalendarCheckIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { useAllCashRemittances } from "../command-center.hooks";

type Remittances = NonNullable<ReturnType<typeof useAllCashRemittances>["data"]>;

/**
 * Cash pipeline: Pending → Received → Collected (PRD §6.4). "Submitted
 * today" is Σ of remittances created today regardless of stage —
 * the stage rows below show where that cash currently sits.
 */
export function CashSummaryCard({
  remittances,
  isLoading,
  canOpenCash,
}: {
  remittances: Remittances;
  isLoading: boolean;
  /** false for roles without /cash access (executive). */
  canOpenCash: boolean;
}) {
  const summary = useMemo(() => {
    const today = new Date().toDateString();
    const sum = (rows: Remittances) => rows.reduce((s, r) => s + r.amount, 0);
    // ⚠ `pending` IS THE ONE STAGE THAT NEEDS THE ₱0 GUARD. Since 0203 a cash
    // booking whose Therapist kept the whole collection still opens a
    // remittance, and it stays `pending` for ever because nothing instructs it.
    // Counted plainly it inflates the Pending row and `uncollectedCount` with
    // hand-overs nobody is waiting for. `received`/`collected` need no guard —
    // a ₱0 row cannot reach either, having no recipient to confirm it.
    const byStatus = (status: string) =>
      status === "pending"
        ? remittances.filter(isOutstandingCash)
        : remittances.filter((r) => r.status === status);
    const todays = remittances.filter(
      (r) => r.created_at && new Date(r.created_at).toDateString() === today,
    );
    return {
      todayTotal: sum(todays),
      todayCount: todays.length,
      pending: byStatus("pending"),
      received: byStatus("received"),
      collected: byStatus("collected"),
      pendingTotal: sum(byStatus("pending")),
      receivedTotal: sum(byStatus("received")),
      collectedTotal: sum(byStatus("collected")),
      uncollectedTotal: sum(byStatus("pending")) + sum(byStatus("received")),
      uncollectedCount: byStatus("pending").length + byStatus("received").length,
    };
  }, [remittances]);

  if (isLoading) {
    return <Skeleton className="h-40 motion-reduce:animate-none" />;
  }

  const stages = [
    {
      label: "Pending",
      count: summary.pending.length,
      total: summary.pendingTotal,
      tone: "text-warning",
      dot: "bg-warning",
    },
    {
      label: "Received",
      count: summary.received.length,
      total: summary.receivedTotal,
      tone: "text-info",
      dot: "bg-info",
    },
    {
      label: "Collected",
      count: summary.collected.length,
      total: summary.collectedTotal,
      tone: "text-success",
      dot: "bg-success",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StatTile
          layout="compact"
          icon={WalletIcon}
          label="Not yet collected"
          tone="warning"
          detail={
            <>
              {summary.uncollectedCount} remittance{summary.uncollectedCount === 1 ? "" : "s"}
            </>
          }
        >
          ₱{summary.uncollectedTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
        </StatTile>
        <StatTile
          layout="compact"
          icon={CalendarCheckIcon}
          label="Submitted today"
          highlight
          detail={
            <>
              {summary.todayCount} remittance{summary.todayCount === 1 ? "" : "s"}
            </>
          }
        >
          ₱{summary.todayTotal.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
        </StatTile>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-medium text-foreground">Remittance register</p>
          <p className="text-xs text-muted-foreground">Last 7 days</p>
        </div>
        <ul className="overflow-hidden rounded-xl border border-border">
          {stages.map((st) => (
            <li
              key={st.label}
              className="flex items-center justify-between gap-3 border-b border-border px-3 py-2.5 last:border-b-0"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className={cn("size-2 shrink-0 rounded-full", st.dot)} />
                <span className={cn("text-sm font-medium", st.tone)}>{st.label}</span>
                <span className="text-xs text-muted-foreground">
                  {st.count} remittance{st.count === 1 ? "" : "s"}
                </span>
              </span>
              <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                ₱{st.total.toLocaleString("en-PH")}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {canOpenCash && (
        <Link
          to="/cash"
          className="flex items-center justify-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          Open cash remittance <ArrowRightIcon size={12} />
        </Link>
      )}
    </div>
  );
}
